import React, { useState, useEffect, useRef, useContext } from "react";
import {
  getTenants,
  createTenant,
  updateTenant,
  deleteTenant,
  deleteTenants,
  getSite,
  getUser,
  isValidPhone,
  isValidPin,
  formatPhone,
  normalizePhoneForSave,
  normalizePinForSave,
  countDigits,
  digitsOnly,
  syncSaltoTenants,
  getLastSaltoSync,
  getTenantsSummary,
} from "./tenantService";
import { useNavigate } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import {
  hasResourcePermission,
  hasAnyResourcePermission,
  PERMISSIONS,
  RESOURCES,
} from "utils/permissions";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasMinimumRole } from "utils/permissions.js";
import HusariaSelect from "components/HusariaSelect";

// reactstrap components
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Media,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Form,
  Label,
} from "reactstrap";

import { getProxy } from "../proxy/proxyService";

import HusariaHeader from "views/pages/components/HusariaHeader";
// core components

const { SearchBar } = Search;

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        Show{" "}
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }{" "}
        entries.
      </label>
    </div>
  ),
});

function Tenants() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteId, setSiteId] = useState(null);
  const [directories, setDirectories] = useState([]);
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [directoryFilter, setDirectoryFilter] = useState("all");
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const fileInputRef = useRef(null);

  // Top-of-form alerts (Add/Edit)
  const [addFormAlert, setAddFormAlert] = useState(null);
  const [editFormAlert, setEditFormAlert] = useState(null);

  const addTopRef = useRef(null);
  const editTopRef = useRef(null);

  const showAddAlert = (color, msg) => {
    setAddFormAlert({ color, msg });
    setTimeout(() => addTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const showEditAlert = (color, msg) => {
    setEditFormAlert({ color, msg });
    setTimeout(() => editTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const TENANTS_RESOURCE = RESOURCES.HRMS_TENANTS;

  const canCreate = hasResourcePermission(
    userData,
    TENANTS_RESOURCE,
    PERMISSIONS.CREATE
  );

  const canEdit = hasResourcePermission(
    userData,
    TENANTS_RESOURCE,
    PERMISSIONS.EDIT
  );

  const canDelete = hasResourcePermission(
    userData,
    TENANTS_RESOURCE,
    PERMISSIONS.DELETE
  );

  const canSyncSalto = hasResourcePermission(
    userData,
    TENANTS_RESOURCE,
    PERMISSIONS.SALTO_SYNC
  );

  const canPerformActions = hasAnyResourcePermission(
    userData,
    TENANTS_RESOURCE,
    [
      PERMISSIONS.CREATE,
      PERMISSIONS.EDIT,
      PERMISSIONS.DELETE,
      PERMISSIONS.SALTO_SYNC,
    ]
  );

  const [saltoSyncLoading, setSaltoSyncLoading] = useState(false);
  const [saltoSyncAlert, setSaltoSyncAlert] = useState(null);
  const [lastSaltoSync, setLastSaltoSync] = useState(null);
  const [lastSaltoSyncLoading, setLastSaltoSyncLoading] = useState(false);
  const [saltoSyncEnabled, setSaltoSyncEnabled] = useState(false);
  const [saltoManagedFields, setSaltoManagedFields] = useState([
    "user_name",
    "v_number",
    "phone1",
  ]);

  const [tenantsSummary, setTenantsSummary] = useState({
    tenants: 0,
    assigned: 0,
    unassigned: 0,
  });

  const infoStyle = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderRadius: "1rem",
    overflow: "hidden",
  };

  const filteredUsers = React.useMemo(() => {
    return users.filter((tenant) => {
      const tenantDirectoryIds = Array.isArray(tenant.dir_uuids)
        ? tenant.dir_uuids.map(String)
        : [];

      const isAssigned = tenantDirectoryIds.length > 0;

      const matchesAssignment =
        assignmentFilter === "all" ||
        (assignmentFilter === "assigned" && isAssigned) ||
        (assignmentFilter === "unassigned" && !isAssigned);

      const matchesDirectory =
        directoryFilter === "all" ||
        tenantDirectoryIds.includes(String(directoryFilter));

      return matchesAssignment && matchesDirectory;
    });
  }, [users, assignmentFilter, directoryFilter]);

  const clearTenantFilters = () => {
    setAssignmentFilter("all");
    setDirectoryFilter("all");
  };

  const loadTenantSummary = React.useCallback(async (currentSiteId) => {
    if (!currentSiteId) {
      setTenantsSummary({
        tenants: 0,
        assigned: 0,
        unassigned: 0,
      });
      return;
    }

    try {
      const summary = await getTenantsSummary(currentSiteId);

      setTenantsSummary({
        tenants: Number(summary?.tenants ?? 0),
        assigned: Number(summary?.assigned ?? 0),
        unassigned: Number(summary?.unassigned ?? 0),
      });
    } catch (err) {
      console.error("Error fetching tenant summary:", err);

      setTenantsSummary({
        tenants: 0,
        assigned: 0,
        unassigned: 0,
      });
    }
  }, []);

  const isSaltoTenant = (tenant) => {
    return Boolean(String(tenant?.salto_ext_user_id ?? "").trim());
  };

  const isSaltoFieldManaged = (tenant, fieldName) => {
    return isSaltoTenant(tenant) && saltoManagedFields.includes(fieldName);
  };

  const SALTO_USER_TOOLTIP = "This user was created through Salto.";
  const SALTO_DELETE_TOOLTIP =
    "Deletion must occur through Salto and then be synced to reflect on HusariaOne.";
  const SALTO_FIELD_TOOLTIP =
    "This field is managed by Salto. Update it in Salto, then run Salto sync to reflect the change in HusariaOne.";

  const formatLastSaltoSync = (value) => {
    if (!value) return "Never";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Unknown";
    }

    return date.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const loadSaltoSyncConfig = React.useCallback(async (currentSiteId) => {
    if (!currentSiteId) {
      setSaltoSyncEnabled(false);
      setSaltoManagedFields(["user_name", "v_number", "phone1"]);
      setLastSaltoSync(null);
      setLastSaltoSyncLoading(false);
      return false;
    }

    try {
      const proxy = await getProxy(currentSiteId);
      const enabled = Boolean(proxy?.salto_sync_enabled);
      const managedFields = ["user_name", "v_number", "phone1"];

      [
        ["phone2", proxy?.salto_phone2_gpf],
        ["phone3", proxy?.salto_phone3_gpf],
        ["pin", proxy?.salto_pin_gpf],
      ].forEach(([fieldName, mapping]) => {
        if (/^GPF[1-5]$/i.test(String(mapping || "").trim())) {
          managedFields.push(fieldName);
        }
      });

      setSaltoSyncEnabled(enabled);
      setSaltoManagedFields(managedFields);

      if (!enabled) {
        setLastSaltoSync(null);
        setLastSaltoSyncLoading(false);
      }

      return enabled;
    } catch (err) {
      console.error("Failed to load Salto sync config:", err);
      setSaltoSyncEnabled(false);
      setSaltoManagedFields(["user_name", "v_number", "phone1"]);
      setLastSaltoSync(null);
      setLastSaltoSyncLoading(false);
      return false;
    }
  }, []);

  const loadLastSaltoSync = React.useCallback(async (currentSiteId, enabledOverride = true) => {
    if (!currentSiteId || !enabledOverride) {
      setLastSaltoSync(null);
      setLastSaltoSyncLoading(false);
      return;
    }

    setLastSaltoSyncLoading(true);

    try {
      const data = await getLastSaltoSync(currentSiteId);
      setLastSaltoSync(data || null);
    } catch (err) {
      console.error("Failed to load last Salto sync:", err);
      setLastSaltoSync(null);
    } finally {
      setLastSaltoSyncLoading(false);
    }
  }, []);

  // Initialize siteId
  useEffect(() => {
    const { site_uuid } = getSite();
    setSiteId(site_uuid || null);
  }, []);

  useEffect(() => {
    setSelectedTenants([]);
    setAssignmentFilter("all");
    setDirectoryFilter("all");
  }, [siteId]);

  useEffect(() => {
    const loadSaltoState = async () => {
      const enabled = await loadSaltoSyncConfig(siteId);

      if (enabled) {
        await loadLastSaltoSync(siteId, true);
      }
    };

    loadSaltoState();
  }, [siteId, loadSaltoSyncConfig, loadLastSaltoSync]);

  useEffect(() => {
    if (!siteId) {
      setDirectories([]);
      return;
    }

    fetch(
      `${serverHost}/hrms/get_directories?site_uuid=${encodeURIComponent(siteId)}`
    )
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Failed to load directories");
        }

        return data;
      })
      .then((data) => {
        setDirectories(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load directories:", err);
        setDirectories([]);
      });
  }, [siteId]);

  const handleSyncSalto = async () => {
    if (!siteId) {
      setSaltoSyncAlert({
        color: "warning",
        msg: "No site selected.",
      });
      return;
    }

    if (!saltoSyncEnabled) {
      setSaltoSyncAlert({
        color: "warning",
        msg: "Salto sync is disabled for this site.",
      });
      return;
    }

    setSaltoSyncLoading(true);
    setSaltoSyncAlert(null);

    try {
      const response = await syncSaltoTenants(siteId);

      const allTenants = await getTenants(siteId);
      const siteTenants = allTenants.filter(
        (t) => String(t.site_uuid) === String(siteId)
      );
      setUsers(siteTenants || []);
      await loadTenantSummary(siteId);

      const result = response?.result || response || {};

      await loadLastSaltoSync(siteId, true);

      let msg = `Salto sync completed. Created: ${result.created ?? 0}, Updated: ${result.updated ?? 0}, Deleted: ${result.deleted ?? 0}, Unchanged: ${result.unchanged ?? 0}, Skipped: ${result.skipped ?? 0}.`;

      if (result.deletion_skipped_reason) {
        msg += ` ${result.deletion_skipped_reason}`;
      }

      setSaltoSyncAlert({
        color: result.deletion_skipped_reason ? "warning" : "success",
        msg,
      });
    } catch (err) {
      console.error("Error syncing Salto tenants:", err);
      setSaltoSyncAlert({
        color: "danger",
        msg: err.message || "Failed to sync Salto tenants.",
      });
    } finally {
      setSaltoSyncLoading(false);
    }
  };

  const tenantCsvHeaders = [
    "tenant_user",
    "virtual_number",
    "phone1",
    "phone2",
    "phone3",
    "phone1_grouped",
    "phone2_grouped",
    "phone3_grouped",
    "pin",
  ];

  const csvEscape = (value = "") => {
    const text = String(value ?? "");

    if (/[",\r\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }

    return text;
  };

  const parseCSVLine = (line) => {
    const values = [];
    let value = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        value += '"';
        i++;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(value);
        value = "";
      } else {
        value += char;
      }
    }

    values.push(value);
    return values;
  };

  const parseCsvBoolean = (value) => {
    const raw = String(value ?? "").trim().toLowerCase();
    return ["true", "1", "yes", "y"].includes(raw);
  };

  const parseDirUuidsFromCsv = (value) => {
    if (Array.isArray(value)) return value;

    return String(value ?? "")
      .split(/[;|]/)
      .map((v) => v.trim())
      .filter(Boolean);
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!users.length) return;

    const csvRows = [tenantCsvHeaders.join(",")];

    users.forEach((user) => {
      const row = [
        user.user_name || "",
        user.v_number || "",
        user.phone1 || "",
        user.phone2 || "",
        user.phone3 || "",
        user.phone1_grouped ? "TRUE" : "FALSE",
        user.phone2_grouped ? "TRUE" : "FALSE",
        user.phone3_grouped ? "TRUE" : "FALSE",
        user.pin || "",
      ].map(csvEscape);

      csvRows.push(row.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tenants.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // CSV Import
  const handleImportCSV = async (event) => {
    if (!window.confirm("Importing CSV will overwrite existing tenant information. Continue?")) {
      event.target.value = null;
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split(/\r\n|\n/).filter((line) => line.trim());

    if (lines.length < 2) {
      alert("CSV file is empty or invalid");
      event.target.value = null;
      return;
    }

    const headers = parseCSVLine(lines[0]).map((h) => h.trim());
    const requiredHeaders = ["tenant_user", "virtual_number", "phone1", "phone2", "phone3"];
    const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

    if (missingHeaders.length > 0) {
      alert(`Missing required columns: ${missingHeaders.join(", ")}`);
      event.target.value = null;
      return;
    }

    const newTenants = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const tenant = {};

      headers.forEach((header, idx) => {
        tenant[header] = values[idx] ? values[idx].trim() : "";
      });

      newTenants.push(tenant);
    }

    try {
      for (const tenant of newTenants) {
        const rowName = tenant.tenant_user || `row ${newTenants.indexOf(tenant) + 2}`;

        const requiredFields = [
          { value: tenant.tenant_user, label: "Tenant User" },
          { value: tenant.virtual_number, label: "Virtual Number" },
          { value: tenant.phone1, label: "Phone 1" },
        ];

        const missingRequired = requiredFields.filter(
          (field) => !String(field.value ?? "").trim()
        );

        if (missingRequired.length > 0) {
          throw new Error(
            `${missingRequired.map((f) => f.label).join(", ")} required (CSV row: ${rowName}).`
          );
        }

        const existing = users.find((u) => u.user_name === tenant.tenant_user);

        if (existing && isSaltoTenant(existing)) {
          throw new Error(
            `${tenant.tenant_user} was created through Salto sync and cannot be overwritten by CSV import.`
          );
        }

        const rawPhones = [
          { value: tenant.phone1, label: "Phone 1", required: true },
          { value: tenant.phone2, label: "Phone 2", required: false },
          { value: tenant.phone3, label: "Phone 3", required: false },
        ];

        for (const p of rawPhones) {
          const raw = String(p.value ?? "").trim();

          if (!isValidPhone(raw, { required: p.required, maxDigits: 11 })) {
            if (p.required && !raw) {
              throw new Error(`${p.label} is required (CSV row: ${rowName}).`);
            }

            throw new Error(
              `${p.label} cannot contain more than 11 digits (CSV row: ${rowName}).`
            );
          }
        }

        if (!isValidPin(tenant.pin)) {
          throw new Error(`PIN must be 2 to 15 digits (CSV row: ${rowName}).`);
        }

        const tenantData = {
          site_uuid: siteId,
          user_name: tenant.tenant_user,
          v_number: tenant.virtual_number,
          phone1: normalizePhoneForSave(tenant.phone1),
          phone2: normalizePhoneForSave(tenant.phone2),
          phone3: normalizePhoneForSave(tenant.phone3),
          phone1_grouped:
            !!String(tenant.phone1 || "").trim() && parseCsvBoolean(tenant.phone1_grouped),
          phone2_grouped:
            !!String(tenant.phone2 || "").trim() && parseCsvBoolean(tenant.phone2_grouped),
          phone3_grouped:
            !!String(tenant.phone3 || "").trim() && parseCsvBoolean(tenant.phone3_grouped),
          pin: normalizePinForSave(tenant.pin),
          dir_uuids: parseDirUuidsFromCsv(tenant.dir_uuids),
        };

        if (existing) {
          await updateTenant(existing.tenant_uuid, { tenant: tenantData });
        } else {
          await createTenant({ tenant: tenantData });
        }
      }

      const allTenants = await getTenants(siteId);
      const siteTenants = allTenants.filter((t) => String(t.site_uuid) === String(siteId));
      setUsers(siteTenants || []);
      await loadTenantSummary(siteId);

      alert("Imported tenants successfully.");
    } catch (err) {
      alert("Error importing tenants: " + err.message);
    }

    event.target.value = null;
  };

  // Modal states
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // Form states
  const [newTenant, setNewTenant] = useState({
    user_name: '',
    v_number: '',
    phone1: '',
    phone2: '',
    phone3: '',
    phone1_grouped: false,
    phone2_grouped: false,
    phone3_grouped: false,
    pin: '',
    dir_uuids: []
  });

  const [editTenant, setEditTenant] = useState({
    tenant_uuid: null,
    site_uuid: null,
    user_name: '',
    v_number: '',
    phone1: '',
    phone2: '',
    phone3: '',
    phone1_grouped: false,
    phone2_grouped: false,
    phone3_grouped: false,
    pin: '',
    dir_uuids: [],
    salto_ext_user_id: null,
  });

  const [deleteTenantId, setDeleteTenantId] = useState(null);

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedTenants,
    nonSelectable: users
      .filter((tenant) => isSaltoTenant(tenant))
      .map((tenant) => tenant.tenant_uuid),
    onSelect: (row, isSelect) => {
      if (isSaltoTenant(row)) return;

      setSelectedTenants((prev) =>
        isSelect
          ? [...prev, row.tenant_uuid]
          : prev.filter((id) => id !== row.tenant_uuid)
      );
    },
    onSelectAll: (isSelect, rows) => {
      const selectableRows = rows.filter((row) => !isSaltoTenant(row));

      setSelectedTenants(
        isSelect ? selectableRows.map((row) => row.tenant_uuid) : []
      );
    },
    classes: "checkbox-column",
    headerClasses: "checkbox-column-header",
  };

  // Modal toggles
  const toggleAddModal = () => {
    const next = !addModal;
    setAddModal(next);

    if (next) {
      setAddFormAlert(null);
      setNewTenant({
        user_name: "",
        v_number: "",
        phone1: "",
        phone2: "",
        phone3: "",
        phone1_grouped: false,
        phone2_grouped: false,
        phone3_grouped: false,
        pin: "",
        dir_uuids: []
      });
    } else {
      setAddFormAlert(null);
    }
  };

  const toggleEditModal = async (tenant = null) => {
    const next = !editModal;
    setEditModal(next);

    if (next) setEditFormAlert(null);
    else setEditFormAlert(null);

    if (tenant) {
      try {
        // Fetch directories linked to tenant
        const res = await fetch(`${serverHost}/hrms/get_tenant_dirs?tenant_uuid=${tenant.tenant_uuid}`);
        const dirsData = res.ok ? await res.json() : [];
        const dirUuids = dirsData.map(d => d.dir_uuid);

        setEditTenant({
          tenant_uuid: tenant.tenant_uuid,
          site_uuid: tenant.site_uuid,
          user_name: tenant.user_name || '',
          v_number: tenant.v_number || '',
          phone1: tenant.phone1 || '',
          phone2: tenant.phone2 || '',
          phone3: tenant.phone3 || '',
          phone1_grouped: !!tenant.phone1_grouped,
          phone2_grouped: !!tenant.phone2_grouped,
          phone3_grouped: !!tenant.phone3_grouped,
          pin: tenant.pin || '',
          dir_uuids: dirUuids,
          salto_ext_user_id: tenant.salto_ext_user_id || null,
        });
      } catch (err) {
        console.error("Failed to load tenant directories:", err);
        setEditTenant({
          tenant_uuid: tenant.tenant_uuid,
          site_uuid: tenant.site_uuid,
          user_name: tenant.user_name || '',
          v_number: tenant.v_number || '',
          phone1: tenant.phone1 || '',
          phone2: tenant.phone2 || '',
          phone3: tenant.phone3 || '',
          phone1_grouped: !!tenant.phone1_grouped,
          phone2_grouped: !!tenant.phone2_grouped,
          phone3_grouped: !!tenant.phone3_grouped,
          pin: tenant.pin || '',
          dir_uuids: [],
          salto_ext_user_id: tenant.salto_ext_user_id || null,
        });
      }
    } else {
      setEditTenant({
        tenant_uuid: null,
        site_uuid: siteId,
        user_name: '',
        v_number: '',
        phone1: '',
        phone2: '',
        phone3: '',
        phone1_grouped: false,
        phone2_grouped: false,
        phone3_grouped: false,
        pin: '',
        dir_uuids: [],
        salto_ext_user_id: null,
      });
    }
  };

  const toggleDeleteModal = (tenantId = null) => {
    setDeleteModal(!deleteModal);
    setDeleteTenantId(tenantId);
  };

  const openDeleteModal = (tenantId) => {
    if (!tenantId) return;
    setDeleteTenantId(tenantId);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
  };


  // Form handlers
  const clearGroupedIfPhoneEmpty = (prev, field, value) => {
    const next = { ...prev, [field]: value };

    const phoneToGrouped = {
      phone1: "phone1_grouped",
      phone2: "phone2_grouped",
      phone3: "phone3_grouped",
    };

    const groupedKey = phoneToGrouped[field];

    if (groupedKey && !String(value || "").trim()) {
      next[groupedKey] = false;
    }

    return next;
  };

  const handleNewTenantChange = (field, value) => {
    setNewTenant((prev) => clearGroupedIfPhoneEmpty(prev, field, value));
  };

  const handleEditTenantChange = (field, value) => {
    setEditTenant((prev) => clearGroupedIfPhoneEmpty(prev, field, value));
  };

  // CRUD handlers
  const handleAddTenant = async () => {
    const requiredFields = [
      { key: "user_name", label: "Name" },
      { key: "v_number", label: "Virtual Number" },
      { key: "phone1", label: "Phone 1" },
    ];

    const phoneFields = [
      { key: "phone1", label: "Phone 1", required: true },
      { key: "phone2", label: "Phone 2", required: false },
      { key: "phone3", label: "Phone 3", required: false },
    ];

    const emptyFields = requiredFields.filter((field) => !String(newTenant[field.key] ?? "").trim());
    if (emptyFields.length > 0) {
      showAddAlert(
        "danger",
        `Please fill out the following required field(s): ${emptyFields.map((f) => f.label).join(", ")}`
      );
      return;
    }

    for (const f of phoneFields) {
      const raw = String(newTenant[f.key] ?? "").trim();

      if (!isValidPhone(raw, { required: f.required, maxDigits: 11 })) {
        if (f.required && !raw) {
          showAddAlert("danger", `${f.label} is required.`);
        } else {
          showAddAlert("danger", `${f.label} cannot contain more than 11 digits.`);
        }
        return;
      }
    }

    if (!isValidPin(newTenant.pin)) {
      showAddAlert("danger", "PIN must be 2 to 15 digits.");
      return;
    }

    try {
      const tenantToSave = {
        ...newTenant,
        site_uuid: siteId,
        phone1: normalizePhoneForSave(newTenant.phone1),
        phone2: normalizePhoneForSave(newTenant.phone2),
        phone3: normalizePhoneForSave(newTenant.phone3),
        phone1_grouped: !!String(newTenant.phone1 || "").trim() && !!newTenant.phone1_grouped,
        phone2_grouped: !!String(newTenant.phone2 || "").trim() && !!newTenant.phone2_grouped,
        phone3_grouped: !!String(newTenant.phone3 || "").trim() && !!newTenant.phone3_grouped,
        pin: normalizePinForSave(newTenant.pin),
      };

      await createTenant({ tenant: tenantToSave });

      const allTenants = await getTenants(siteId);
      setUsers(allTenants.filter((t) => t.site_uuid === siteId));
      await loadTenantSummary(siteId);

      showAddAlert("success", "Tenant added successfully.");
      setTimeout(() => toggleAddModal(), 600);
    } catch (err) {
      console.error("Error adding tenant:", err);
      showAddAlert("danger", err?.message ? `Failed to add tenant: ${err.message}` : "Failed to add tenant.");
    }
  };

  const handleEditSave = async () => {

    const requiredFields = [
      { key: "user_name", label: "Name" },
      { key: "v_number", label: "Virtual Number" },
      { key: "phone1", label: "Phone 1" },
    ];

    const phoneFields = [
      { key: "phone1", label: "Phone 1", required: true },
      { key: "phone2", label: "Phone 2", required: false },
      { key: "phone3", label: "Phone 3", required: false },
    ];

    const emptyFields = requiredFields.filter((field) => {
      const value = editTenant[field.key];
      return !value || String(value).trim() === "";
    });

    if (emptyFields.length > 0) {
      showEditAlert(
        "danger",
        `Please fill out the following required field(s): ${emptyFields.map((f) => f.label).join(", ")}`
      );
      return;
    }

    for (const f of phoneFields) {
      const raw = String(editTenant[f.key] ?? "").trim();

      if (!isValidPhone(raw, { required: f.required, maxDigits: 11 })) {
        if (f.required && !raw) {
          showEditAlert("danger", `${f.label} is required.`);
        } else {
          showEditAlert("danger", `${f.label} cannot contain more than 11 digits.`);
        }
        return;
      }
    }

    if (!isValidPin(editTenant.pin)) {
      showEditAlert("danger", "PIN must be 2 to 15 digits.");
      return;
    }

    try {
      const tenantToSave = {
        ...editTenant,
        phone1: normalizePhoneForSave(editTenant.phone1),
        phone2: normalizePhoneForSave(editTenant.phone2),
        phone3: normalizePhoneForSave(editTenant.phone3),
        phone1_grouped: !!String(editTenant.phone1 || "").trim() && !!editTenant.phone1_grouped,
        phone2_grouped: !!String(editTenant.phone2 || "").trim() && !!editTenant.phone2_grouped,
        phone3_grouped: !!String(editTenant.phone3 || "").trim() && !!editTenant.phone3_grouped,
        pin: normalizePinForSave(editTenant.pin),
      };

      await updateTenant(editTenant.tenant_uuid, { tenant: tenantToSave });

      const allTenants = await getTenants(siteId);
      setUsers(allTenants.filter((t) => t.site_uuid === siteId));
      await loadTenantSummary(siteId);

      showEditAlert("success", "Tenant updated successfully.");
      setTimeout(() => toggleEditModal(), 600);
    } catch (err) {
      console.error("Error updating tenant:", err);
      showEditAlert("danger", err?.message ? `Failed to update tenant: ${err.message}` : "Failed to update tenant.");
    }
  };

  const handleDelete = async () => {
    const tenantToDelete = users.find((u) => u.tenant_uuid === deleteTenantId);

    if (isSaltoTenant(tenantToDelete)) {
      setSaltoSyncAlert({
        color: "warning",
        msg: "This tenant was created through Salto sync and cannot be deleted.",
      });
      closeDeleteModal();
      return;
    }

    try {
      await deleteTenant(deleteTenantId);
      setUsers(users.filter(u => u.tenant_uuid !== deleteTenantId));
      await loadTenantSummary(siteId);
      toggleDeleteModal();
    } catch (err) {
      console.error("Error deleting tenant:", err);
      setSaltoSyncAlert({
        color: "danger",
        msg: err.message || "Failed to delete tenant.",
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTenants.length === 0) return;

    const allowedTenantIds = selectedTenants.filter((tenantUuid) => {
      const tenant = users.find((u) => u.tenant_uuid === tenantUuid);
      return tenant && !isSaltoTenant(tenant);
    });

    if (allowedTenantIds.length === 0) {
      setSaltoSyncAlert({
        color: "warning",
        msg: "Salto synced tenants cannot be deleted.",
      });
      setSelectedTenants([]);
      return;
    }

    try {
      await deleteTenants(allowedTenantIds);

      setUsers(prev =>
        prev.filter(u => !allowedTenantIds.includes(u.tenant_uuid))
      );

      setSelectedTenants([]);
      await loadTenantSummary(siteId);
    } catch (err) {
      console.error("Error deleting tenants:", err);
      setSaltoSyncAlert({
        color: "danger",
        msg: err.message || "Failed to delete selected tenants.",
      });
    }
  };

  // Initialize siteId and listen for changes
  useEffect(() => {
    const site = getSite();
    setSiteId(site.site_uuid);

    const handleSiteChange = (event) => {
      setSiteId(event.detail);
    };

    window.addEventListener("siteChanged", handleSiteChange);
    return () => window.removeEventListener("siteChanged", handleSiteChange);
  }, []);

  // Fetch tenants whenever siteId changes
  useEffect(() => {
    const getTenantsForSite = async () => {
      if (!siteId) {
        setUsers([]);
        setLoading(false);
        await loadTenantSummary(null);
        return;
      }

      setLoading(true);

      try {
        const allTenants = await getTenants(siteId);
        const siteTenants = allTenants.filter(
          (t) => String(t.site_uuid) === String(siteId)
        );

        setUsers(siteTenants || []);
        await loadTenantSummary(siteId);
      } catch (error) {
        console.error("Error fetching tenants:", error);
        setUsers([]);
        await loadTenantSummary(null);
      } finally {
        setLoading(false);
      }
    };

    getTenantsForSite();
  }, [siteId, loadTenantSummary]);

  const renderPhoneInputWithGroupCall = ({
    form,
    isEdit = false,
    phoneKey,
    groupedKey,
    label,
    required = false,
    onChange,
    inputDisabled = false,
    disabledTooltip = null,
  }) => {
    const inputId = isEdit ? `edit_${phoneKey}` : phoneKey;
    const groupedId = isEdit ? `edit_${groupedKey}` : groupedKey;
    const tooltipTargetId = `${inputId}_salto_lock_tooltip`;

    return (
      <FormGroup>
        <Label for={inputId}>
          {label} {required && <span className="text-danger">*</span>}
        </Label>

        <div id={tooltipTargetId}>
          <Input
            type="text"
            name={phoneKey}
            id={inputId}
            placeholder={`Enter ${label.toLowerCase()}`}
            value={form[phoneKey]}
            disabled={inputDisabled}
            onChange={(e) => onChange(phoneKey, e.target.value)}
            style={{
              cursor: inputDisabled ? "not-allowed" : undefined,
            }}
          />
        </div>

        {inputDisabled && disabledTooltip && (
          <UncontrolledTooltip delay={0} target={tooltipTargetId}>
            {disabledTooltip}
          </UncontrolledTooltip>
        )}

        <FormGroup check className="mt-2">
          <Label check for={groupedId}>
            <Input
              type="checkbox"
              id={groupedId}
              checked={!!form[groupedKey]}
              disabled={!String(form[phoneKey] || "").trim()}
              onChange={(e) => onChange(groupedKey, e.target.checked)}
            />
            {" "}Group Call to Next Number
          </Label>
        </FormGroup>
      </FormGroup>
    );
  };

  const editTenantIsSaltoSynced = isSaltoTenant(editTenant);
  const editPhone2IsSaltoManaged = isSaltoFieldManaged(editTenant, "phone2");
  const editPhone3IsSaltoManaged = isSaltoFieldManaged(editTenant, "phone3");
  const editPinIsSaltoManaged = isSaltoFieldManaged(editTenant, "pin");

  const lastSaltoSyncText = !siteId
    ? "No site selected"
    : saltoSyncLoading
      ? "Sync running..."
      : lastSaltoSyncLoading
        ? "Loading..."
        : formatLastSaltoSync(lastSaltoSync?.last_synced_at);

  const lastSaltoSyncSummary =
    saltoSyncEnabled && lastSaltoSync?.last_synced_at
      ? `(Created: ${lastSaltoSync.created_count ?? 0}, Updated: ${lastSaltoSync.updated_count ?? 0}, Deleted: ${lastSaltoSync.deleted_count ?? 0}, Unchanged: ${lastSaltoSync.unchanged_count ?? 0}, Skipped: ${lastSaltoSync.skipped_count ?? 0})`
      : "";

  return (
    <>
      <HusariaHeader name="Tenants" parentName="HRMS" padding="9rem" />
      <Container
        fluid
        className={`mt--8 ${isDark ? "table-dark-mode" : ""}`}
      >
        {/* ===================== STATISTICS CARDS ===================== */}
        <Row className="mb-4">
          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={infoStyle}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Number of Tenants
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700 }}>
                      {tenantsSummary.tenants ?? 0}
                    </h2>
                  </div>
                  <i className="fas fa-users" style={{ fontSize: "2.5rem" }} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={infoStyle}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Assigned Tenants
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700, color: "#28a745" }}>
                      {tenantsSummary.assigned ?? 0}
                    </h2>
                  </div>
                  <i className="fas fa-check-circle text-success" style={{ fontSize: "2.5rem" }} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={infoStyle}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Unassigned Tenants
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700, color: "#dc3545" }}>
                      {tenantsSummary.unassigned ?? 0}
                    </h2>
                  </div>
                  <i className="fas fa-times-circle text-danger" style={{ fontSize: "2.5rem" }} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <div className="col">
            <Card>
              {saltoSyncAlert && (
                <Alert
                  color={saltoSyncAlert.color}
                  toggle={() => setSaltoSyncAlert(null)}
                  className="m-3 mb-0"
                >
                  {saltoSyncAlert.msg}
                </Alert>
              )}
              <ToolkitProvider
                keyField="tenant_uuid"
                data={filteredUsers}
                columns={[
                  {
                    dataField: "user_name",
                    text: "Tenant",
                    sort: true,
                    formatter: (cell, row) => {
                      const isSynced = isSaltoTenant(row);
                      const saltoBadgeId = `salto-badge-${row.tenant_uuid}`;

                      return (
                        <Media className="align-items-center">
                          <a
                            className="avatar rounded-circle mr-3"
                            href="#pablo"
                            onClick={(e) => e.preventDefault()}
                            style={{
                              filter: isSynced ? "grayscale(0.15)" : "none",
                              opacity: isSynced ? 0.95 : 1,
                            }}
                          >
                            <img
                              alt="..."
                              src={require("assets/img/theme/tenant-icon.png")}
                            />
                          </a>

                          <Media>
                            <span className="name mb-0 text-sm">
                              {cell || "-"}

                              {isSynced && (
                                <>
                                  <span
                                    id={saltoBadgeId}
                                    className="badge badge-secondary ml-2"
                                  >
                                    Salto
                                  </span>

                                  <UncontrolledTooltip delay={0} target={saltoBadgeId}>
                                    {SALTO_USER_TOOLTIP}
                                  </UncontrolledTooltip>
                                </>
                              )}
                            </span>
                          </Media>
                        </Media>
                      );
                    },
                  },
                  {
                    dataField: "v_number",
                    text: "Virtual Number",
                    sort: true,
                    formatter: (cell) => cell || "-",
                  },
                  {
                    dataField: "phone1",
                    text: "Phone 1",
                    sort: true,
                    formatter: (cell) => formatPhone(cell),
                  },
                  {
                    dataField: "phone2",
                    text: "Phone 2",
                    sort: true,
                    formatter: (cell) => formatPhone(cell),
                  },
                  {
                    dataField: "phone3",
                    text: "Phone 3",
                    sort: true,
                    formatter: (cell) => formatPhone(cell),
                  },
                  {
                    dataField: "actions",
                    text: "Actions",
                    hidden: !canPerformActions,
                    sort: true,
                    sortValue: (cell, row) => {
                      return isSaltoTenant(row) ? "Salto" : "HusariaOne";
                    },
                    formatter: (cell, row) => {
                      if (!canPerformActions) return null;

                      if (isSaltoTenant(row)) {
                        const actionTooltipId = `salto-action-${row.tenant_uuid}`;

                        return (
                          <>
                            <span
                              id={actionTooltipId}
                              className="text"
                            >
                              N/A
                            </span>

                            <UncontrolledTooltip delay={0} target={actionTooltipId}>
                              {SALTO_DELETE_TOOLTIP}
                            </UncontrolledTooltip>
                          </>
                        );
                      }

                      return (
                        <div
                          className="table-actions d-flex"
                          style={{ gap: "8px", flexWrap: "wrap" }}
                        >
                          {canDelete && (
                            <Button
                              size="sm"
                              color="danger"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleDeleteModal(row.tenant_uuid);
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      );
                    },
                  }
                ]}
                search
              >
                {(props) => (
                  <>
                    <CardHeader
                      className="border-0 header-theme" style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                      }}>
                      <Row className="align-items-center">
                        <Col xs="6" className="d-flex align-items-center">
                          <Form className="navbar-search navbar-search-light form-inline mr-sm-3">
                            <FormGroup className="mb-0">
                              <InputGroup className="input-group-alternative input-group-merge search-border">
                                <InputGroupText>
                                  <i className="fas fa-search" style={{ color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)" }} />
                                </InputGroupText>
                                <SearchBar
                                  className="form-control table-search-bar"
                                  placeholder="Search"
                                  {...props.searchProps}
                                />
                              </InputGroup>
                            </FormGroup>
                          </Form>
                        </Col>
                        <Col className="text-right" xs="6" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          {canPerformActions && (
                            <>
                              {selectedTenants.length > 0 && canDelete && (
                                <Button
                                  className="btn-round btn-icon"
                                  color="danger"
                                  onClick={() => setBulkDeleteModal(true)}
                                  size="sm"
                                >
                                  <span className="btn-inner--icon mr-1">
                                    <i className="fas fa-trash" />
                                  </span>
                                  <span className="btn-inner--text">Delete Selected ({selectedTenants.length})</span>
                                </Button>
                              )}

                              {canSyncSalto && saltoSyncEnabled && (
                                <>
                                  <Button
                                    className="btn-round btn-icon"
                                    color="success"
                                    id="tooltip-sync-salto"
                                    onClick={handleSyncSalto}
                                    size="sm"
                                    disabled={saltoSyncLoading}
                                  >
                                    <span className="btn-inner--icon mr-1">
                                      <i className="fas fa-sync" />
                                    </span>
                                    <span className="btn-inner--text">
                                      {saltoSyncLoading ? "Syncing Salto..." : "Sync Salto"}
                                    </span>
                                  </Button>

                                  <UncontrolledTooltip delay={0} target="tooltip-sync-salto">
                                    Import eligible Salto users into tenants
                                  </UncontrolledTooltip>
                                </>
                              )}

                              <Button
                                className="btn-round btn-icon"
                                color="primary"
                                id="tooltip-export-csv"
                                onClick={() => handleExportCSV()}
                                size="sm"
                              >
                                <span className="btn-inner--icon mr-1">
                                  <i className="fas fa-file-export" />
                                </span>
                                <span className="btn-inner--text">Export CSV</span>
                              </Button>
                              <UncontrolledTooltip delay={0} target="tooltip-export-csv">
                                Export tenants to CSV
                              </UncontrolledTooltip>

                              <Button
                                className="btn-round btn-icon"
                                color="light"
                                id="tooltip-import-csv"
                                onClick={() => fileInputRef.current.click()}
                                size="sm"
                              >
                                <span className="btn-inner--icon mr-1">
                                  <i className="fas fa-file-import" />
                                </span>
                                <span className="btn-inner--text">Import CSV</span>
                              </Button>
                              <UncontrolledTooltip delay={0} target="tooltip-import-csv">
                                Import tenants from CSV
                              </UncontrolledTooltip>

                              <Button
                                className="btn-round btn-icon"
                                color="info"
                                href="#pablo"
                                id="tooltip-add-new"
                                onClick={toggleAddModal}
                                size="sm"
                              >
                                <span className="btn-inner--icon mr-1">
                                  <i className="fas fa-plus" />
                                </span>
                                <span className="btn-inner--text">New</span>
                              </Button>
                              <UncontrolledTooltip delay={0} target="tooltip-add-new">
                                Add New Tenant
                              </UncontrolledTooltip>

                              <input
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleImportCSV}
                              />
                            </>
                          )}
                        </Col>
                      </Row>

                      <div
                        className="d-flex align-items-center flex-wrap mt-3 px-3 py-2"
                        style={{
                          gap: "12px",
                          border: `1px solid ${isDark ? "#374151" : "#e9ecef"}`,
                          borderRadius: "10px",
                          backgroundColor: isDark ? "#111827" : "#f8f9fa",
                        }}
                      >
                        {/* Assignment filters */}
                        <div
                          className="d-flex align-items-center p-1"
                          style={{
                            gap: "4px",
                            borderRadius: "8px",
                            backgroundColor: isDark ? "#1f2937" : "#e9ecef",
                          }}
                        >
                          {[
                            {
                              value: "all",
                              label: "All",
                              count: users.length,
                            },
                            {
                              value: "assigned",
                              label: "Assigned",
                              count: users.filter(
                                (tenant) =>
                                  Array.isArray(tenant.dir_uuids) &&
                                  tenant.dir_uuids.length > 0
                              ).length,
                            },
                            {
                              value: "unassigned",
                              label: "Unassigned",
                              count: users.filter(
                                (tenant) =>
                                  !Array.isArray(tenant.dir_uuids) ||
                                  tenant.dir_uuids.length === 0
                              ).length,
                            },
                          ].map((option) => {
                            const active = assignmentFilter === option.value;

                            return (
                              <Button
                                key={option.value}
                                type="button"
                                size="sm"
                                color={active ? "primary" : "link"}
                                className="px-3 py-2 mb-0 shadow-none"
                                style={{
                                  borderRadius: "6px",
                                  color: active
                                    ? "#fff"
                                    : isDark
                                      ? "#cbd5e1"
                                      : "#101013",
                                  textDecoration: "none",
                                }}
                                onClick={() => {
                                  setAssignmentFilter(option.value);

                                  if (option.value === "unassigned") {
                                    setDirectoryFilter("all");
                                  }
                                }}
                              >
                                {option.label}

                                <span
                                  className="ml-1"
                                  style={{
                                    opacity: active ? 0.85 : 0.65,
                                  }}
                                >
                                  {option.count}
                                </span>
                              </Button>
                            );
                          })}
                        </div>

                        {/* Directory filter */}
                        <div
                          className="d-flex align-items-center"
                          style={{ gap: "8px" }}
                        >
                          <i
                            className="fas fa-folder"
                            style={{
                              color: isDark ? "#94a3b8" : "#8898aa",
                            }}
                          />

                          <Input
                            type="select"
                            bsSize="sm"
                            aria-label="Filter tenants by directory"
                            value={directoryFilter}
                            style={{
                              minWidth: "210px",
                              borderRadius: "7px",
                            }}
                            onChange={(e) => {
                              const value = e.target.value;

                              setDirectoryFilter(value);

                              if (value !== "all") {
                                setAssignmentFilter("assigned");
                              }
                            }}
                          >
                            <option value="all">All directories</option>

                            {directories.map((directory) => (
                              <option
                                key={directory.dir_uuid}
                                value={directory.dir_uuid}
                              >
                                {directory.dir_name}
                              </option>
                            ))}
                          </Input>
                        </div>

                        {/* Result count and reset button */}
                        <div
                          className="d-flex align-items-center ml-lg-auto"
                          style={{ gap: "10px" }}
                        >
                          <span
                            className="small"
                            style={{
                              color: isDark ? "#ffffff" : "#000000",
                            }}
                          >
                            {filteredUsers.length} of {users.length} tenants
                          </span>

                          {(assignmentFilter !== "all" ||
                            directoryFilter !== "all") && (
                              <Button
                                type="button"
                                color="link"
                                size="sm"
                                className="p-0 mb-0 shadow-none"
                                onClick={clearTenantFilters}
                              >
                                <i className="fas fa-times mr-1" />
                                Reset
                              </Button>
                            )}
                        </div>
                      </div>

                      {saltoSyncEnabled && (
                        <Row className="mt-2">
                          <Col>
                            <div
                              id="last-salto-sync-display"
                              className="d-flex align-items-center flex-wrap small"
                              style={{
                                gap: "6px",
                                color: isDark ? "#cbd5e1" : "#6c757d",
                              }}
                            >
                              <i className="fas fa-clock" />

                              <strong style={{ color: isDark ? "#e2e8f0" : "#525f7f" }}>
                                Last Salto sync:
                              </strong>

                              <span>{lastSaltoSyncText}</span>

                              {lastSaltoSyncSummary && (
                                <span className="ml-2">
                                  {lastSaltoSyncSummary}
                                </span>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )}
                    </CardHeader>
                    <div className="py-4 table-responsive">
                      <BootstrapTable
                        {...props.baseProps}
                        bootstrap4={true}
                        pagination={pagination}
                        bordered={false}
                        hover
                        rowStyle={() => ({
                          cursor: canEdit ? "pointer" : "default",
                        })}
                        rowClasses={(row) => {
                          return isSaltoTenant(row) ? "salto-synced-row" : "";
                        }}
                        selectRow={canDelete ? selectRow : undefined}
                        rowEvents={{
                          onClick: (e, row) => {
                            if (!canEdit) return;
                            if (e.target?.closest("button")) return;
                            if (e.target?.closest("input")) return;
                            if (e.target?.closest(".badge")) return;

                            toggleEditModal(row);
                          },
                        }}
                        classes="align-items-center table-flush"
                        noDataIndication={() =>
                          loading ? (
                            <div className="text-center">Loading...</div>
                          ) : (
                            <div className="text-center">
                              {users.length > 0
                                ? "No tenants match the selected filters."
                                : "No tenants found for the selected site."}
                            </div>
                          )
                        }
                      />
                    </div>
                  </>
                )}
              </ToolkitProvider>
            </Card>
          </div>
        </Row>
      </Container >

      {/* Add New Tenant Modal */}
      < Modal isOpen={addModal} toggle={toggleAddModal} >
        <ModalHeader style={{
          backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
          color: isDark ? "#e2e8f0" : "#111",
        }} toggle={toggleAddModal}>Add New Tenant</ModalHeader>
        <ModalBody className="card-body">
          <div ref={addTopRef} />

          {addFormAlert && (
            <Alert
              color={addFormAlert.color}
              toggle={() => setAddFormAlert(null)}
              fade
            >
              {addFormAlert.msg}
            </Alert>
          )}
          <FormGroup>
            <Label for="user_name">
              Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="user_name"
              id="user_name"
              placeholder="Enter tenant name"
              value={newTenant.user_name}
              onChange={(e) => handleNewTenantChange('user_name', e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="v_number">
              Virtual Number <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="v_number"
              id="v_number"
              placeholder="Enter virtual number"
              value={newTenant.v_number}
              onChange={(e) => handleNewTenantChange('v_number', digitsOnly(e.target.value))}
            />
          </FormGroup>
          <FormGroup>
            <Label for="dir_uuids">Directories</Label>
            <HusariaSelect
              isMulti
              name="dir_uuids"
              id="dir_uuids"
              options={directories.map(d => ({ value: d.dir_uuid, label: d.dir_name }))}
              value={(newTenant.dir_uuids || []).map(id => {
                const dir = directories.find(d => d.dir_uuid === id);
                return dir ? { value: dir.dir_uuid, label: dir.dir_name } : null;
              }).filter(Boolean)}
              onChange={(selectedOptions) => {
                const selectedIds = selectedOptions.map(opt => opt.value);
                setNewTenant(prev => ({ ...prev, dir_uuids: selectedIds }));
              }}
              placeholder="Select directories..."
            />
          </FormGroup>
          {renderPhoneInputWithGroupCall({
            form: newTenant,
            phoneKey: "phone1",
            groupedKey: "phone1_grouped",
            label: "Phone 1",
            required: true,
            onChange: handleNewTenantChange,
          })}

          {renderPhoneInputWithGroupCall({
            form: newTenant,
            phoneKey: "phone2",
            groupedKey: "phone2_grouped",
            label: "Phone 2",
            onChange: handleNewTenantChange,
          })}

          {renderPhoneInputWithGroupCall({
            form: newTenant,
            phoneKey: "phone3",
            groupedKey: "phone3_grouped",
            label: "Phone 3",
            onChange: handleNewTenantChange,
          })}
          <hr />
          <h5 className="mb-3">Access Permissions</h5>

          <FormGroup>
            <Label for="pin">PIN</Label>
            <Input
              type="text"
              name="pin"
              id="pin"
              placeholder="Enter optional PIN"
              value={newTenant.pin}
              onChange={(e) => handleNewTenantChange('pin', digitsOnly(e.target.value))}
            />
            <small className="text-muted">
              Optional. Must be 2 to 15 digits.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="card-body">
          <Button color="danger" onClick={toggleAddModal}>
            Cancel
          </Button>
          <Button color="success" onClick={handleAddTenant}>
            Add Tenant
          </Button>
        </ModalFooter>
      </Modal >

      {/* Edit Tenant Modal */}
      < Modal isOpen={editModal} toggle={() => toggleEditModal()
      }>
        <ModalHeader style={{
          backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
          color: isDark ? "#e2e8f0" : "#111",
        }} toggle={toggleEditModal}>Edit Tenant</ModalHeader>
        <ModalBody className="card-body">
          <div ref={editTopRef} />

          {editFormAlert && (
            <Alert
              color={editFormAlert.color}
              toggle={() => setEditFormAlert(null)}
              fade
            >
              {editFormAlert.msg}
            </Alert>
          )}
          <FormGroup>
            <Label for="edit_user_name">
              Name <span className="text-danger">*</span>
            </Label>

            <div id="edit_user_name_salto_lock_tooltip">
              <Input
                type="text"
                name="user_name"
                id="edit_user_name"
                placeholder="Enter tenant name"
                value={editTenant.user_name}
                disabled={editTenantIsSaltoSynced}
                onChange={(e) => handleEditTenantChange('user_name', e.target.value)}
                style={{
                  cursor: editTenantIsSaltoSynced ? "not-allowed" : undefined,
                }}
              />
            </div>

            {editTenantIsSaltoSynced && (
              <UncontrolledTooltip delay={0} target="edit_user_name_salto_lock_tooltip">
                {SALTO_FIELD_TOOLTIP}
              </UncontrolledTooltip>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="edit_v_number">
              Virtual Number <span className="text-danger">*</span>
            </Label>

            <div id="edit_v_number_salto_lock_tooltip">
              <Input
                type="text"
                name="v_number"
                id="edit_v_number"
                placeholder="Enter virtual number"
                value={editTenant.v_number}
                disabled={editTenantIsSaltoSynced}
                onChange={(e) => handleEditTenantChange('v_number', digitsOnly(e.target.value))}
                style={{
                  cursor: editTenantIsSaltoSynced ? "not-allowed" : undefined,
                }}
              />
            </div>

            {editTenantIsSaltoSynced && (
              <UncontrolledTooltip delay={0} target="edit_v_number_salto_lock_tooltip">
                {SALTO_FIELD_TOOLTIP}
              </UncontrolledTooltip>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="edit_dir_uuids">Directories</Label>
            <HusariaSelect
              isMulti
              name="edit_dir_uuids"
              id="edit_dir_uuids"
              options={directories.map(d => ({ value: d.dir_uuid, label: d.dir_name }))}
              value={(editTenant.dir_uuids || []).map(id => {
                const dir = directories.find(d => d.dir_uuid === id);
                return dir ? { value: dir.dir_uuid, label: dir.dir_name } : null;
              }).filter(Boolean)}
              onChange={(selectedOptions) => {
                const selectedIds = selectedOptions.map(opt => opt.value);
                setEditTenant(prev => ({ ...prev, dir_uuids: selectedIds }));
              }}
              placeholder="Select directories..."
            />
          </FormGroup>
          {renderPhoneInputWithGroupCall({
            form: editTenant,
            isEdit: true,
            phoneKey: "phone1",
            groupedKey: "phone1_grouped",
            label: "Phone 1",
            required: true,
            onChange: handleEditTenantChange,
            inputDisabled: editTenantIsSaltoSynced,
            disabledTooltip: SALTO_FIELD_TOOLTIP,
          })}

          {renderPhoneInputWithGroupCall({
            form: editTenant,
            isEdit: true,
            phoneKey: "phone2",
            groupedKey: "phone2_grouped",
            label: "Phone 2",
            onChange: handleEditTenantChange,
            inputDisabled: editPhone2IsSaltoManaged,
            disabledTooltip: SALTO_FIELD_TOOLTIP,
          })}

          {renderPhoneInputWithGroupCall({
            form: editTenant,
            isEdit: true,
            phoneKey: "phone3",
            groupedKey: "phone3_grouped",
            label: "Phone 3",
            onChange: handleEditTenantChange,
            inputDisabled: editPhone3IsSaltoManaged,
            disabledTooltip: SALTO_FIELD_TOOLTIP,
          })}

          <hr />
          <h5 className="mb-3">Access Permissions</h5>

          <FormGroup>
            <Label for="edit_pin">PIN</Label>

            <div id="edit_pin_salto_lock_tooltip">
              <Input
                type="text"
                name="pin"
                id="edit_pin"
                placeholder="Enter optional PIN"
                value={editTenant.pin || ""}
                disabled={editPinIsSaltoManaged}
                onChange={(e) => handleEditTenantChange('pin', digitsOnly(e.target.value))}
                style={{
                  cursor: editPinIsSaltoManaged ? "not-allowed" : undefined,
                }}
              />
            </div>

            {editPinIsSaltoManaged && (
              <UncontrolledTooltip delay={0} target="edit_pin_salto_lock_tooltip">
                {SALTO_FIELD_TOOLTIP}
              </UncontrolledTooltip>
            )}

            <small className="text-muted">
              Optional. Must be 2 to 15 digits.
            </small>
          </FormGroup>
        </ModalBody >
        <ModalFooter className="card-body">
          <Button color="danger" onClick={() => toggleEditModal()}>
            Cancel
          </Button>
          <Button color="success" onClick={handleEditSave}>
            Save Changes
          </Button>
        </ModalFooter>
      </Modal >

      {/* Delete Confirmation Modal */}
      < Modal isOpen={deleteModal} toggle={closeDeleteModal} centered >
        <ModalHeader style={{
          backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
          color: isDark ? "#e2e8f0" : "#111",
        }} toggle={closeDeleteModal}>Delete Tenant</ModalHeader>
        <ModalBody className="card-body">
          Are you sure you want to delete this tenant?
          <br />
          <strong>This action cannot be undone.</strong>
        </ModalBody>
        <ModalFooter className="card-body">
          <Button color="light" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal >

      <Modal isOpen={bulkDeleteModal} toggle={() => setBulkDeleteModal(false)} centered>
        <ModalHeader
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
          }}
          toggle={() => setBulkDeleteModal(false)}
        >
          Delete Tenants
        </ModalHeader>

        <ModalBody className="card-body">
          Delete <strong>{selectedTenants.length}</strong> selected tenants?
          <br />
          <strong>This action cannot be undone.</strong>
        </ModalBody>

        <ModalFooter className="card-body">
          <Button color="light" onClick={() => setBulkDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={async () => {
              await handleBulkDelete();
              setBulkDeleteModal(false);
            }}
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>

    </>
  );
}

export default Tenants;
